import React, { useState, useEffect } from 'react';
import { 
    FiEye, FiX, FiUser, FiPhone, FiMail, FiMapPin, 
    FiCalendar, FiAlertTriangle, FiChevronLeft, FiChevronRight 
} from "react-icons/fi";
import { useDashboard } from '../Context/Dashboardcontext';
import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next'; // 1. Import hook

const IncidentReports = () => {
    // 2. Initialize translation hook
    const { t } = useTranslation();
    
    // Access Incident Data AND userProfile from Context
    // ✅ EXTRACTED userProfile HERE
    const { FetchAllIncidentReports, allIncidentReports, userProfile } = useDashboard();

    // State for Pagination and Modal
    const [currentPage, setCurrentPage] = useState(1);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [selectedIncident, setSelectedIncident] = useState(null);

    // Fetch Incidents whenever currentPage changes
    useEffect(() => {
        FetchAllIncidentReports(currentPage);
    }, [currentPage]);

    // Handler to open modal
    const handleViewIncident = (incident) => {
        setSelectedIncident(incident);
        setIsReportModalOpen(true);
    };

    // Handler to close modal
    const closeReportModal = () => {
        setIsReportModalOpen(false);
        setSelectedIncident(null);
    };

    // Pagination Handlers
    const handleNextPage = () => {
        if (allIncidentReports?.data?.hasNextPage) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (allIncidentReports?.data?.hasPrevPage) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    // Helper to safely get the list AND apply AI Package logic
    const getIncidentList = () => {
        const docs = allIncidentReports?.data?.docs || [];
        
        // --- AI PACKAGE FILTER LOGIC ---
        // If the user has an AI package (aiPackageId exists), return all incident reports.
        // If they don't, filter the reports to only show those where companyType is 'Dispax'.
        return userProfile?.aiPackageId 
            ? docs 
            : docs.filter(report => report?.companyType === 'Dispax');
    };

    // Helper to get pagination info
    const paginationInfo = allIncidentReports?.data || {};

    return (
        <div className="text-white relative px-2 md:px-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <h1 className="text-2xl font-semibold">{t('incidentReports.title')}</h1>
            </div>

            {/* Incident List Table */}
            <div className="overflow-x-auto bg-[#1D2461] rounded-lg shadow-lg mb-4">
                <div className="min-w-[800px] md:min-w-0">
                    <table className="w-full table-auto text-sm text-left">
                        <thead className="bg-[#B2BAFF] text-[#202224] font-nunito uppercase text-[14px]">
                            <tr>
                                <th className="p-4">{t('incidentReports.table.driver')}</th>
                                <th className="p-4">{t('incidentReports.table.type')}</th>
                                <th className="p-4">{t('incidentReports.table.dateTime')}</th>
                                <th className="p-4">{t('incidentReports.table.location')}</th>
                                <th className="p-4">{t('incidentReports.table.reportedBy')}</th>
                                <th className="p-4 text-center">{t('incidentReports.table.action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getIncidentList().length > 0 ? (
                                getIncidentList().map((report) => (
                                    <tr key={report._id} className="border-t border-[#2A2A4D] hover:bg-[#2A2A4D] transition-colors">
                                        {/* Driver Info */}
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <img 
                                                    src={report.userId?.image || "https://ui-avatars.com/api/?name=User&background=random"} 
                                                    alt="driver" 
                                                    className="w-8 h-8 rounded-full object-cover"
                                                />
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-white">{report.userId?.fullName || t('common.unknown')}</span>
                                                    <span className="text-xs text-gray-400">{report.userId?.Id || "N/A"}</span>
                                                </div>
                                            </div>
                                        </td>
                                        
                                        {/* Type */}
                                        <td className="p-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                report.incidentType === 'Crash' ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300'
                                            }`}>
                                                {report.incidentType}
                                            </span>
                                        </td>

                                        {/* Date & Time */}
                                        <td className="p-4">
                                            <div className="flex flex-col">
                                                <span className="text-white">
                                                    {report.date ? new Date(report.date).toLocaleDateString() : "N/A"}
                                                </span>
                                                <span className="text-xs text-gray-400">{report.time || "N/A"}</span>
                                            </div>
                                        </td>

                                        {/* Location */}
                                        <td className="p-4">{report.location || t('common.unknown')}</td>

                                        {/* Reported By */}
                                        <td className="p-4">{report.reportedBy || "N/A"}</td>

                                        {/* Action - View Only */}
                                        <td className="p-4 text-center">
                                            <button 
                                                onClick={() => handleViewIncident(report)}
                                                className="bg-[#6F1AFF] hover:bg-[#5815CC] text-white p-2 rounded-lg transition-colors"
                                                title={t('incidentReports.actions.viewDetails')}
                                            >
                                                <FiEye size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center text-gray-400">
                                        {t('incidentReports.table.emptyState')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Controls */}
            {paginationInfo.totalPages > 1 && (
                <div className="flex justify-between items-center bg-[#1D2461] p-4 rounded-lg shadow-lg">
                    <div className="text-sm text-gray-300">
                        {t('incidentReports.pagination.showing')} <span className="font-bold text-white">{paginationInfo.page}</span> {t('incidentReports.pagination.of')} <span className="font-bold text-white">{paginationInfo.totalPages}</span>
                        <span className="text-xs text-gray-500 ml-2">({paginationInfo.totalDocs} {t('incidentReports.pagination.totalIncidents')})</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={handlePrevPage} 
                            disabled={!paginationInfo.hasPrevPage}
                            className={`p-2 rounded-lg flex items-center gap-1 text-sm transition-all ${
                                !paginationInfo.hasPrevPage 
                                ? 'bg-[#2A2A4D] text-gray-500 cursor-not-allowed' 
                                : 'bg-[#6F1AFF] text-white hover:bg-[#5815CC]'
                            }`}
                        >
                            <FiChevronLeft /> {t('incidentReports.pagination.previous')}
                        </button>

                        <div className="flex items-center gap-1">
                            <span className="px-3 py-1 bg-[#2A2A4D] rounded text-white text-sm">
                                {currentPage}
                            </span>
                        </div>

                        <button 
                            onClick={handleNextPage} 
                            disabled={!paginationInfo.hasNextPage}
                            className={`p-2 rounded-lg flex items-center gap-1 text-sm transition-all ${
                                !paginationInfo.hasNextPage 
                                ? 'bg-[#2A2A4D] text-gray-500 cursor-not-allowed' 
                                : 'bg-[#6F1AFF] text-white hover:bg-[#5815CC]'
                            }`}
                        >
                            {t('incidentReports.pagination.next')} <FiChevronRight />
                        </button>
                    </div>
                </div>
            )}

            {/* Incident View Modal */}
            {isReportModalOpen && selectedIncident && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1000] p-4 backdrop-blur-sm">
                    <div 
                        className="rounded-2xl shadow-2xl text-white p-6 relative w-full max-w-2xl max-h-[90vh] overflow-y-auto hide-scrollbar border border-[#2A2A4D]"
                        style={{ background: "radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)" }}
                    >
                        {/* Modal Header */}
                        <div className="flex justify-between items-center mb-6 border-b border-[#2A2A4D] pb-4">
                            <div>
                                <h2 className="text-xl font-bold text-white">{t('incidentReports.modal.title')}</h2>
                                <span className="text-sm text-gray-400">{t('incidentReports.modal.reportId')}: {selectedIncident._id}</span>
                            </div>
                            <button onClick={closeReportModal} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                                <FiX size={24} />
                            </button>
                        </div>

                        <div className="space-y-6">
                            
                            {/* --- Section 1: Driver Information --- */}
                            <div className="bg-[#10194E] p-5 rounded-xl border border-[#2A2A4D]">
                                <h3 className="text-md font-semibold text-white mb-4 flex items-center gap-2">
                                    <FiUser className="text-[#6F1AFF]" /> {t('incidentReports.modal.driverInfo')}
                                </h3>
                                <div className="flex items-start gap-4">
                                    <img 
                                        src={selectedIncident.userId?.image || "https://ui-avatars.com/api/?name=User&background=random"} 
                                        alt="Driver Profile" 
                                        className="w-16 h-16 rounded-full object-cover border-2 border-[#6F1AFF]"
                                    />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 w-full">
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase">{t('incidentReports.modal.fullName')}</p>
                                            <p className="text-white font-medium">{selectedIncident.userId?.fullName || "N/A"}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase">{t('incidentReports.modal.driverId')}</p>
                                            <p className="text-white font-medium">{selectedIncident.userId?.Id || "N/A"}</p>
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <FiPhone className="text-gray-400 text-xs" />
                                            <span className="text-sm text-gray-200">{selectedIncident.userId?.mobileNumber || "N/A"}</span>
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <FiMail className="text-gray-400 text-xs" />
                                            <span className="text-sm text-gray-200">{selectedIncident.userId?.email || "N/A"}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* --- Section 2: Incident Details Grid --- */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Column 1 */}
                                <div className="space-y-4">
                                    <div className="bg-[#10194E] p-4 rounded-xl">
                                        <p className="text-xs text-gray-400 uppercase mb-1 flex items-center gap-2"><FiAlertTriangle/> {t('incidentReports.table.type')}</p>
                                        <p className={`text-lg font-medium ${selectedIncident.incidentType === 'Crash' ? 'text-red-400' : 'text-blue-400'}`}>
                                            {selectedIncident.incidentType}
                                        </p>
                                    </div>
                                    <div className="bg-[#10194E] p-4 rounded-xl">
                                        <p className="text-xs text-gray-400 uppercase mb-1 flex items-center gap-2"><FiUser/> {t('incidentReports.table.reportedBy')}</p>
                                        <p className="font-medium text-white">{selectedIncident.reportedBy}</p>
                                    </div>
                                </div>
                                {/* Column 2 */}
                                <div className="space-y-4">
                                    <div className="bg-[#10194E] p-4 rounded-xl">
                                        <p className="text-xs text-gray-400 uppercase mb-1 flex items-center gap-2"><FiCalendar/> {t('incidentReports.table.dateTime')}</p>
                                        <p className="font-medium text-white">
                                            {new Date(selectedIncident.date).toLocaleDateString()} <span className="text-gray-400 mx-1">|</span> <span className="text-sm">{selectedIncident.time}</span>
                                        </p>
                                    </div>
                                    <div className="bg-[#10194E] p-4 rounded-xl">
                                        <p className="text-xs text-gray-400 uppercase mb-1 flex items-center gap-2"><FiMapPin/> {t('incidentReports.table.location')}</p>
                                        <p className="font-medium text-white">{selectedIncident.location}</p>
                                    </div>
                                </div>
                            </div>

                            {/* --- Section 3: Description --- */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-300 mb-2">{t('incidentReports.modal.description')}</h3>
                                <div className="bg-[#10194E] p-4 rounded-xl text-sm leading-relaxed text-gray-200 min-h-[60px]">
                                    {selectedIncident.description || t('incidentReports.modal.noDescription')}
                                </div>
                            </div>

                            {/* --- Section 4: Dispatcher Notes --- */}
                            {selectedIncident.dispatcherNotes && (
                                <div>
                                    <h3 className="text-sm font-semibold text-orange-300 mb-2">{t('incidentReports.modal.dispatcherNotes')}</h3>
                                    <div className="bg-[#10194E] p-4 rounded-xl text-sm text-gray-200 border-l-4 border-orange-500">
                                        {selectedIncident.dispatcherNotes}
                                    </div>
                                </div>
                            )}

                            {/* --- Section 5: Photos Gallery --- */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-300 mb-3">
                                    {t('incidentReports.modal.evidence')} ({selectedIncident.photos?.length || 0})
                                </h3>
                                {selectedIncident.photos && selectedIncident.photos.length > 0 ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {selectedIncident.photos.map((photo, index) => (
                                            <a 
                                                key={photo._id || index} 
                                                href={photo.image} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="block group relative overflow-hidden rounded-lg border border-[#2A2A4D] aspect-video"
                                            >
                                                <img 
                                                    src={photo.image} 
                                                    alt={`Evidence ${index + 1}`} 
                                                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                                                />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                    <FiEye className="text-white text-xl" />
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-[#10194E] p-4 rounded-xl text-center">
                                        <p className="text-sm text-gray-500 italic">{t('incidentReports.modal.noEvidence')}</p>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            )}

            <ToastContainer theme="colored" />
        </div>
    );
};

export default IncidentReports;